import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';

export interface ServiceProvider {
  id: string;
  name: string;
  category: string;
  phone: string | null;
  email: string | null;
  addressCep: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressNeighborhood: string | null;
  addressCity: string | null;
  addressState: string | null;
  internalRating: number | null;
  notes: string | null;
  active: boolean;
  osCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProviderData {
  name: string;
  category: string;
  phone?: string;
  email?: string;
  addressCep?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressComplement?: string;
  addressNeighborhood?: string;
  addressCity?: string;
  addressState?: string;
  internalRating?: number;
  notes?: string;
}

export interface UpdateProviderData {
  name?: string;
  category?: string;
  phone?: string | null;
  email?: string | null;
  addressCep?: string | null;
  addressStreet?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  addressNeighborhood?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  internalRating?: number | null;
  notes?: string | null;
  active?: boolean;
}

export function useServiceProviders(filters?: { category?: string }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const wid = user?.workshopId;

  const params = new URLSearchParams();
  if (filters?.category) params.set('category', filters.category);
  const qs = params.toString();

  const providersQuery = useQuery<{ providers: ServiceProvider[] }>({
    queryKey: ['service-providers', wid, filters],
    queryFn: async () => {
      const res = await api.get(
        `/workshops/${wid}/service-providers${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    enabled: !!wid,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateProviderData) => {
      const res = await api.post(`/workshops/${wid}/service-providers`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-providers'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: UpdateProviderData & { id: string }) => {
      const res = await api.patch(`/workshops/${wid}/service-providers/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-providers'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/workshops/${wid}/service-providers/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-providers'] });
    },
  });

  return {
    providers: providersQuery.data?.providers ?? [],
    isLoading: providersQuery.isLoading,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deactivate: deactivateMutation.mutateAsync,
  };
}
